import { useState } from "react";
import { ago, post, del } from "../lib/api";
import type { AgentTurn, TurnStep } from "../lib/api";
import { Card, Pill, Empty, Carregando, Button } from "../lib/ui";
import { ThumbsDown, Undo2 } from "lucide-react";

/**
 * O que o agente pensou e fez em cada turno.
 *
 * Quando a IA responde algo estranho, a mensagem final sozinha não explica
 * nada — ajustar prompt olhando só a saída é adivinhação. Aqui está o
 * raciocínio, cada ferramenta chamada, o que ela devolveu, e por que o turno
 * terminou como terminou.
 */
/*
 * Os turnos chegam de fora.
 *
 * Eles servem a duas telas agora -- este rastro e a linha de detalhe embaixo de
 * cada resposta, na conversa. Cada um com a sua busca seria o mesmo dado
 * assinado duas vezes, e as duas telas podendo discordar na tela.
 */
export function Rastro({
  turns: data,
  onMudou,
}: {
  turns: AgentTurn[] | null;
  onMudou: () => void;
}) {
  if (!data) return <Carregando />;
  if (data.length === 0) {
    return <Empty>A IA ainda não respondeu nada nesta conversa.</Empty>;
  }

  // A rota devolve os 30 turnos mais recentes. Com 30 na mao pode haver mais
  // atras, e o total diz isso em vez de fingir que e o da conversa inteira.
  const custo = data.reduce((s, t) => s + Number(t.cost_usd || 0), 0);
  const parcial = data.length >= 30;

  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-3 tabular-nums">
        {data.length} turno{data.length > 1 ? "s" : ""} · US$ {custo.toFixed(2)}
        {parcial ? " nos 30 mais recentes" : " nesta conversa"}
      </p>
      {data.map((t) => (
        <Turno key={t.id} t={t} onMudou={onMudou} />
      ))}
    </div>
  );
}

function Turno({ t, onMudou }: { t: AgentTurn; onMudou: () => void }) {
  const [aberto, setAberto] = useState(false);
  const rodando = t.finished_at === null;

  const ferramentas = t.steps.filter((s) => s.tipo === "ferramenta");

  return (
    <Card
      stripe={
        t.error ? "danger" : rodando ? "accent" : t.aborted ? "warm" : undefined
      }
      className="overflow-hidden"
    >
      <button
        onClick={() => setAberto((v) => !v)}
        className="w-full text-left px-3 py-2.5 hover:bg-surface-2 transition-colors"
      >
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-ink-3 text-xs">{aberto ? "▾" : "▸"}</span>

          {rodando ? (
            <span className="text-accent flex items-center gap-1.5">
              <span className="pulse">●</span> pensando…
            </span>
          ) : (
            <span className="text-ink">
              {t.trigger === "followup" ? "follow-up" : "respondeu"}
            </span>
          )}

          {ferramentas.length > 0 && (
            <span className="text-xs text-ink-3">
              {ferramentas.length} ferramenta{ferramentas.length > 1 ? "s" : ""}
            </span>
          )}

          {t.outcome.escalou === true && <Pill tone="warm">escalou</Pill>}
          {typeof t.outcome.fatos === "number" && t.outcome.fatos > 0 && (
            <Pill tone="accent">{t.outcome.fatos} fatos</Pill>
          )}
          {t.aborted && <Pill tone="warm">{rotuloAborto(t.aborted)}</Pill>}
          {t.error && <Pill tone="danger">erro</Pill>}
          {t.ruim && <Pill tone="danger">marcada como ruim</Pill>}

          <span className="ml-auto flex items-center gap-3 text-xs text-ink-3 tabular-nums">
            {t.duration_ms != null && <span>{(t.duration_ms / 1000).toFixed(1)}s</span>}
            {t.chamadas != null && (
              <span title="Idas à API: 1 + uma por rodada de ferramentas">
                {t.chamadas} {t.chamadas === 1 ? "chamada" : "chamadas"}
              </span>
            )}
            {t.cost_usd > 0 && <span>US$ {Number(t.cost_usd).toFixed(4)}</span>}
            <span>{ago(t.started_at)}</span>
          </span>
        </div>

        {!aberto && t.reply && (
          <p className="text-xs text-ink-2 mt-1 pl-4 truncate">{t.reply}</p>
        )}
      </button>

      {aberto && (
        <div className="border-t border-rule px-3 py-3 space-y-3 bg-surface-2/40">
          {t.thinking && (
            <Bloco titulo="Raciocínio" cor="text-accent">
              <p className="whitespace-pre-wrap text-ink-2 leading-relaxed">
                {t.thinking}
              </p>
            </Bloco>
          )}

          {t.steps.map((s, i) => (
            <Passo key={i} s={s} />
          ))}

          {t.reply && (
            <Bloco titulo="Enviado ao lead" cor="text-ok">
              <div className="space-y-1.5">
                {t.reply.split("\n\n").map((b, i) => (
                  <p
                    key={i}
                    className="bg-ok-dim border border-ok/25 rounded-lg px-2.5 py-1.5 text-ink"
                  >
                    {b}
                  </p>
                ))}
              </div>
            </Bloco>
          )}

          {t.aborted && !t.error && (
            <Bloco titulo="Não enviou" cor="text-warm">
              <p className="text-ink-2">{explicarAborto(t.aborted)}</p>
            </Bloco>
          )}

          {t.error && (
            <Bloco titulo="Erro" cor="text-danger">
              <p className="text-danger font-mono text-xs">{t.error}</p>
            </Bloco>
          )}

          <Ruim t={t} onMudou={onMudou} />

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-3 pt-1 border-t border-rule tabular-nums">
            <span>modelo {t.model}</span>
            <span>{t.tokens_in} entrada</span>
            <span>{t.tokens_cached} do cache</span>
            {t.tokens_cache_write > 0 && <span>{t.tokens_cache_write} gravados</span>}
            <span>{t.tokens_out} saída</span>
            {t.tokens_in + t.tokens_cached > 0 && (
              <span
                title="Abaixo de 70% em conversa longa, algo está invalidando o prefixo cacheado"
                className={
                  t.tokens_cached / (t.tokens_in + t.tokens_cached) < 0.5
                    ? "text-warm"
                    : ""
                }
              >
                cache{" "}
                {Math.round(
                  (t.tokens_cached / (t.tokens_in + t.tokens_cached)) * 100,
                )}
                %
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function Passo({ s }: { s: TurnStep }) {
  // O texto final aparece no bloco "Enviado". A excecao e o que o filtro
  // descartou: isso o operador precisa ver, e nao existe em nenhum outro lugar.
  if (s.tipo === "texto") {
    if (!s.texto.startsWith("[DESCARTADO:")) return null;
    return (
      <Bloco titulo="Descartado (não foi enviado)" cor="text-danger">
        <p className="whitespace-pre-wrap text-danger/90 bg-danger-dim border border-danger/25 rounded-lg px-2.5 py-1.5">
          {s.texto}
        </p>
      </Bloco>
    );
  }

  const erro = s.saida.startsWith("ERRO:");
  return (
    <Bloco titulo={s.nome} cor={erro ? "text-danger" : "text-warm"} sufixo={`${s.ms}ms`}>
      <div className="space-y-1.5">
        <pre className="text-xs text-ink-2 whitespace-pre-wrap font-mono overflow-x-auto">
          {JSON.stringify(s.entrada, null, 2)}
        </pre>
        <p className={`text-xs pl-2 border-l-2 ${erro ? "border-danger text-danger" : "border-rule-2 text-ink-2"} whitespace-pre-wrap`}>
          {s.saida}
        </p>
      </div>
    </Bloco>
  );
}

function Bloco({
  titulo,
  cor,
  sufixo,
  children,
}: {
  titulo: string;
  cor: string;
  sufixo?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${cor}`}>
          {titulo}
        </span>
        {sufixo && <span className="text-[11px] text-ink-3 tabular-nums">{sufixo}</span>}
      </div>
      <div className="text-xs">{children}</div>
    </div>
  );
}

function rotuloAborto(a: string): string {
  if (a === "lock") return "corretor assumiu";
  if (a === "saida_invalida")
    return "O modelo devolveu texto que não era resposta ao cliente — chamada de ferramenta escrita como texto, ou raciocínio interno. Nada foi enviado, e a conversa foi passada para o corretor. O texto descartado aparece acima, marcado como DESCARTADO.";
  if (a === "interrompido") return "interrompido";
  if (a === "erro") return "falhou";
  if (a === "sem_historico") return "sem histórico";
  if (a === "saida_invalida") return "saída inválida";
  return a;
}

function explicarAborto(a: string): string {
  if (a === "lock")
    return "O corretor assumiu a conversa enquanto o modelo gerava. A resposta foi descartada antes de sair — que é o certo: a IA não pode falar por cima de quem está atendendo.";
  if (a === "interrompido")
    return "O turno ficou aberto por mais de dois minutos e foi encerrado. Processo caiu no meio ou a API não respondeu.";
  if (a === "sem_historico")
    return "Não havia conversa anterior para retomar.";
  return a;
}

/**
 * "Esta resposta foi ruim."
 *
 * Fica junto do raciocínio e das ferramentas de propósito: é aqui que dá para
 * ver POR QUE ela respondeu aquilo, e é isso que a revisão precisa. O
 * comentário é opcional — exigir texto faria a pessoa não marcar, e marcar sem
 * explicar já diz alguma coisa.
 *
 * Nada muda sozinho: isto vira material para a revisão, não ajuste automático.
 */
function Ruim({ t, onMudou }: { t: AgentTurn; onMudou: () => void }) {
  const [abrindo, setAbrindo] = useState(false);
  const [texto, setTexto] = useState(t.ruim_comentario ?? "");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    try {
      await post(`/turns/${t.id}/ruim`, { comentario: texto });
      setAbrindo(false);
      onMudou();
    } finally {
      setSalvando(false);
    }
  }

  if (t.ruim && !abrindo) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs rounded-lg bg-danger-dim border border-danger/25 px-3 py-2">
        <ThumbsDown size={13} className="text-danger shrink-0" />
        <span className="text-ink-2 flex-1 min-w-0">
          {t.ruim_comentario || "Marcada como ruim, sem comentário."}
        </span>
        <button
          onClick={() => setAbrindo(true)}
          className="text-ink-3 hover:text-ink underline underline-offset-2"
        >
          editar
        </button>
        <button
          onClick={async () => {
            await del(`/turns/${t.id}/ruim`);
            onMudou();
          }}
          className="text-ink-3 hover:text-ink inline-flex items-center gap-1"
        >
          <Undo2 size={12} /> desmarcar
        </button>
      </div>
    );
  }

  if (!abrindo) {
    return (
      <button
        onClick={() => setAbrindo(true)}
        className="inline-flex items-center gap-1.5 text-xs text-ink-3 hover:text-danger transition-colors"
      >
        <ThumbsDown size={13} />
        Marcar como resposta ruim
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg bg-surface-2 border border-rule p-3">
      <label className="block space-y-1.5">
        <span className="text-xs font-medium text-ink-2">
          O que ela deveria ter dito? (opcional)
        </span>
        <input
          autoFocus
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void salvar()}
          placeholder="Ex.: ofereceu horário vago em vez de duas horas cheias"
          className="w-full h-9 px-3 text-sm"
        />
      </label>
      <div className="flex gap-2">
        <Button variant="primary" className="h-8" onClick={salvar} disabled={salvando}>
          Salvar
        </Button>
        <Button variant="ghost" className="h-8" onClick={() => setAbrindo(false)}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
